import axios, { AxiosError } from 'axios';

// Bug #227: extend AxiosRequestConfig with a per-request opt-out for the
// global "401 → redirect to /login" behaviour. Used by `fetchDocumentBlob`
// so a 401 from the document /content proxy can be caught and handled with
// a fallback rather than evicting the user from the page.
declare module 'axios' {
  export interface AxiosRequestConfig {
    skipAuthRedirect?: boolean;
  }
}

// VITE_API_BASE_URL holds the backend origin (no path). We always suffix /api/v1
// here so individual callers don't have to know about the prefix.
// Bug #204: exported so components that build direct-navigation URLs (e.g.
// document Open / Preview) can resolve relative paths against the backend
// origin instead of the SPA origin.
export const API_ORIGIN = import.meta.env.VITE_API_BASE_URL || '';
const API_BASE_URL = `${API_ORIGIN}/api/v1`;

class PermissionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PermissionError';
  }
}

class NetworkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NetworkError';
  }
}

let authTokenGetter: (() => Promise<string>) | null = null;

const client = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: Add auth token and correlation ID
client.interceptors.request.use(
  async (config) => {
    if (authTokenGetter) {
      try {
        const token = await authTokenGetter();
        config.headers.Authorization = `Bearer ${token}`;
      } catch (error) {
        console.error('Failed to acquire token:', error);
        throw error;
      }
    }

    config.headers['X-Correlation-ID'] = crypto.randomUUID();
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: Handle errors
client.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    // Bug #227: callers may opt out of the global "401 → redirect to /login"
    // behaviour by setting `skipAuthRedirect: true` on their request config.
    // The document /content proxy uses this so a 401 there doesn't yank an
    // already-authenticated reviewer back to the login page — the caller has
    // its own fallback path (direct storage URL) and just wants the rejection.
    const requestCfg = (error.config || {}) as { skipAuthRedirect?: boolean };
    if (error.response?.status === 401 && !requestCfg.skipAuthRedirect) {
      // Avoid a redirect loop if we're already on the login page.
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }

    if (error.response?.status === 403) {
      return Promise.reject(
        new PermissionError('You do not have permission to access this resource')
      );
    }

    if (error.code === 'ERR_NETWORK' || !error.response) {
      return Promise.reject(
        new NetworkError('Network error. Please check your connection and try again.')
      );
    }

    return Promise.reject(error);
  }
);

export function setAuthTokenGetter(getter: () => Promise<string>): void {
  authTokenGetter = getter;
}

export default client;
